import { Dialog } from "@/design-system/Dialog/Dialog";
import { clubCrestUrl } from "@/features/MyClub/Layout/MyClubSidePanel";
import { AppDispatch } from "@/redux/store";
import {
  getUserSelector,
  usedFeature,
  userProfileAnsweredSelector,
} from "@/redux/user/userSlice";
import { safeGetItem, safeSetItem } from "@/utils/safeStorage";
import { useTranslation } from "@repo/shared/i18n";
import { clubStatsSelector } from "@repo/shared/redux";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";

/**
 * "Welcome to your club", shown once per user on the club home. Users who had
 * a club before the redesign see it too: the page changed enough to introduce.
 *
 * Seen-ness lives on the account (`usedFeatures`), so it follows the user
 * across devices. It waits for the profile to answer, so a returning user is
 * never flashed the dialog while their used features are still unknown.
 */
/* Also kept on this browser: should the account fail to record it (a backend
   without the feature, a lost request), the welcome still never comes back. */
const SEEN_KEY = "MY_CLUB_WELCOME_SEEN";

export const MyClubWelcomeDialog = () => {
  const { t } = useTranslation(["myClub", "common"]);
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector(getUserSelector);
  const profileAnswered = useSelector(userProfileAnsweredSelector);
  const stats = useSelector(clubStatsSelector);
  // Closed at once on the click; the account catches up when the post lands.
  const [dismissed, setDismissed] = useState(false);

  const seen =
    user?.usedFeatures?.includes("MY_CLUB_WELCOME") ||
    safeGetItem(SEEN_KEY) === "1";
  const isOpen = profileAnswered && !!user?.uuid && !seen && !dismissed;

  const close = () => {
    setDismissed(true);
    safeSetItem(SEEN_KEY, "1");
    dispatch(usedFeature({ feature: "MY_CLUB_WELCOME" }));
  };

  /*
    Get started hands over to the onboarding in "For your club", whose
    second step builds the best squad. The dialog only says hello.
  */
  const getStarted = close;

  return (
    <Dialog
      isOpen={isOpen}
      onClose={close}
      onPositive={getStarted}
      title=""
      showClose={false}
      negLabel=""
      posLabel={t("welcome.cta")}
      width={420}
    >
      <div className="flex flex-col items-center gap-4 pt-2 text-center">
        {stats.clubBadgeId > 0 && (
          <img
            src={clubCrestUrl(stats.clubBadgeId)}
            alt=""
            className="size-13 object-contain"
          />
        )}
        <div className="flex flex-col gap-2.5">
          <h2 className="text-xl leading-tight font-semibold text-foreground">
            {t("welcome.title")}
          </h2>
          <p className="text-sm leading-relaxed text-pretty text-foreground-muted mobile:text-sm">
            {t("welcome.body")}
          </p>
        </div>
      </div>
    </Dialog>
  );
};
